import { Routes, Route } from "react-router-dom";
import React, { Suspense } from "react";
import ProtectedRoute from "./ProtectedRoute";
import Main from "../Main";
import SalesPortalMain from "../SalesPortalMain";
import SalesDashboard from "../SalesDashboard/SalesDashboard";
import RmPortalMain from "../RmPortal/RmPortalMain";
import RmDashboard from "../RmPortal/Dashboard/RmDashboard";
import RmEntrepreneurs from "../RmPortal/Entrepreneurs/RmEntrepreneurs";
import RmInvestors from "../RmPortal/Investors/RmInvestors";
import RmMentors from "../RmPortal/Mentors/RmMentors";
import RmInteractionLogs from "../RmPortal/Logs/RmInteractionLogs";
import RmProfileDetails from "../RmPortal/Details/RmProfileDetails";

// ✅ ALL lazy imports MUST be outside the component function
// Having them inside causes React to recreate them on every render,
// which breaks context (including Redux Provider) — this was your root cause.

const Login = React.lazy(() => import("../../Components/Login/Login"));
const ForgotPassword = React.lazy(
  () => import("../../Components/Login/ForgotPassword"),
);
const ResetLinkSent = React.lazy(
  () => import("../../Components/Login/ResetLinkSent"),
);
const ResetPassword = React.lazy(
  () => import("../../Components/Login/ResetPassword"),
);
const CheckEmailForForgetPassword = React.lazy(
  () => import("../../Components/Login/VerifyEmailForForgetPassword"),
);

const Dashboard = React.lazy(() => import("../Dashboard/dashboard"));
const UserList = React.lazy(() => import("../ManageUser/UserList"));
const EntrepreneurList = React.lazy(
  () => import("../ManagerEntrepreneur/List/EntrepreneurList"),
);
const MentorList = React.lazy(() => import("../ManageMentors/MentorsList"));
const MentorSessions = React.lazy(
  () => import("../ManageMentors/MentorSessions/MentorSessions"),
);
const AddMentor = React.lazy(() => import("../ManageMentors/AddMentor"));
const ViewMentorDetailsCommon = React.lazy(
  () => import("../ManageMentors/MentorDetails_common"),
);
const MentorPaymentsList = React.lazy(
  () => import("../ManageMentors/MentorPaymentsList"),
);
const PayoutRequestsList = React.lazy(
  () => import("../ManageMentors/PayoutRequestsList"),
);
const InvestorList = React.lazy(() => import("../ManageInvestor/InvestorList"));
const AddInvestor = React.lazy(() => import("../ManageInvestor/AddInvestor"));
const AddEntrepreneur = React.lazy(
  () => import("../ManagerEntrepreneur/AddEntrepreneur"),
);
const ViewInvestorDetailsCommon = React.lazy(
  () => import("../ManageInvestor/InvestorDetails_common"),
);
const ViewEnterprneurDetailsCommon = React.lazy(
  () => import("../ManagerEntrepreneur/Business_Card_Details"),
);
const Analysis = React.lazy(() => import("../Analysis/profile_analysis_premium"));
const BannerList = React.lazy(() => import("../ManageBanner/BannerList"));
const PromotionList = React.lazy(
  () => import("../ManagePromotion/PromotionList"),
);
const ForumList = React.lazy(
  () => import("../ManageInvestorForum/InvestorForumList"),
);
const ForumDetails = React.lazy(
  () => import("../ManageInvestorForum/Investorforumdetails"),
);
const NetworkForumList = React.lazy(
  () => import("../ManageNetworkForum/NetworkForumList"),
);
const NetworkForumDetails = React.lazy(
  () => import("../ManageNetworkForum/NetworkForumDetails"),
);

const SubscriptionList = React.lazy(
  () => import("../ManageSubscription/SubscriptionList"),
);
const AddSubscription = React.lazy(
  () => import("../ManageSubscription/AddSubscription"),
);
const NotificationList = React.lazy(
  () => import("../ManageNotification/NotificationList"),
);
const PersonalizedNotificationList = React.lazy(
  () => import("../ManagePersonalizedNotification/PersonalizedNotificationList"),
);
const NotificationLogs = React.lazy(
  () => import("../ManageNotification/NotificationLogs"),
);
const ManualNotificationLogs = React.lazy(
  () => import("../ManageNotification/ManualNotificationLogs"),
);
const ViewNotification = React.lazy(
  () => import("../ManageNotification/ViewNotification"),
);
const AddCms = React.lazy(() => import("../ManageCMS/CmsList"));
const BlogsandEventsList = React.lazy(
  () => import("../ManageBlogs&Events/Blogs&EventList"),
);
const EditRole = React.lazy(() => import("../ManageRoles/AddRole"));
const AddRole = React.lazy(() => import("../ManageRoles/AddRole"));
const ManageRoles = React.lazy(
  () => import("../../Components/ManageRoles/RoleList"),
);
const TransactionListing = React.lazy(
  () => import("../ManageTransaction/TransactionListing"),
);
const ActiveSubscriptionListing = React.lazy(
  () => import("../ManageActiveSubscription/ActiveSubscriptionListing"),
);
const CustomerQueriesListing = React.lazy(
  () => import("../ManageCustomerQueries/CustomerQueriesListing"),
);
const ManageCoupons = React.lazy(
  () => import("../ManageCoupons/CouponListing"),
);
const EditProfile = React.lazy(() => import("../../Components/Common/EditProfile"));
const AddCoupon = React.lazy(
  () => import("../ManageCoupons/Addcoupon"),
);
const ManageDeactivatedAccounts = React.lazy(
  () => import("../DeactivatedAccounts/DeactivatedAccountList"),
);
const EmailLogDetails = React.lazy(
  () => import("../ManagerEntrepreneur/List/EmailDraft/EmailLogDetails"),
);
const EmailManagement = React.lazy(
  () => import("../ManageEmail/EmailManagement"),
);
const SessionListing = React.lazy(
  () => import("../ManageSessions/SessionListing"),
);
const UpdateKnowledgeBase = React.lazy(
  () => import("../ManageKnowledgeBase/UpdateKnowledgeBase"),
);

const Navigation = () => {
  return (
    <div id="main-wraper">
      {/* ✅ Single top-level Suspense — no nested Suspense per route needed */}
      <Suspense fallback={<></>}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/link-sent" element={<ResetLinkSent />} />

          <Route
            path="/"
            element={
              <ProtectedRoute authenticationPath="/login" allowedRole="admin" outlet={<Main />} />
            }
          >
            <Route
              path="/verify-email/forget-password/:url"
              element={<CheckEmailForForgetPassword />}
            />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/edit-profile" element={<EditProfile />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/manage-roles" element={<ManageRoles />} />
            <Route path="/manage-users" element={<UserList />} />
            <Route path="/manage-entrepreneur" element={<EntrepreneurList />} />
            <Route
              path="/manage-entrepreneur/add"
              element={<AddEntrepreneur />}
            />
            <Route
              path="/manage-entrepreneur/edit/:id"
              element={<AddEntrepreneur />}
            />
            <Route
              path="/manage-entrepreneur/edit/:id/:type"
              element={<AddEntrepreneur />}
            />
            <Route path="/manage-mentors" element={<MentorList />} />
            <Route path="/manage-mentors/add" element={<AddMentor />} />
            <Route path="/manage-mentors/edit/:id" element={<AddMentor />} />
            <Route
              path="/manage-mentors/:id"
              element={<ViewMentorDetailsCommon />}
            />
            <Route
              path="/manage-mentors/:uuid/sessions"
              element={<MentorSessions />}
            />
            <Route path="/mentor-payments" element={<MentorPaymentsList />} />
            <Route path="/mentor-payouts" element={<PayoutRequestsList />} />
            <Route
              path="/manage-entrepreneur/:id/:type"
              element={<ViewEnterprneurDetailsCommon />}
            />
            <Route
              path="/profile-analysis/:type/:userUuid"
              element={<Analysis />}
            />
            <Route
              path="/:login_type/email-logs/:id"
              element={<EmailLogDetails />}
            />
            <Route path="/manage-investor/add" element={<AddInvestor />} />
            <Route
              path="/manage-investor/edit/:id"
              element={<AddInvestor />}
            />
            <Route
              path="/manage-investor/:id"
              element={<ViewInvestorDetailsCommon />}
            />
            <Route path="/manage-investor" element={<InvestorList />} />
            <Route path="/manage-banners" element={<BannerList />} />
            <Route path="/manage-promotions" element={<PromotionList />} />
            <Route path="/manage-forum-post" element={<ForumList />} />
            <Route path="/view-forum-post/:uuid" element={<ForumDetails />} />
            <Route path="/manage-network-forum" element={<NetworkForumList />} />
            <Route path="/view-network-forum/:uuid" element={<NetworkForumDetails />} />
            <Route path="/manage-network-hub" element={<NetworkForumList />} />
            <Route path="/view-network-hub/:uuid" element={<NetworkForumDetails />} />
            <Route path="/manage-subscription" element={<SubscriptionList />} />
            <Route path="/manage-notification" element={<NotificationList />} />
            <Route path="/personalized-notifications" element={<PersonalizedNotificationList />} />
            <Route path="/notification-logs" element={<NotificationLogs />} />
            <Route path="/ai-notification-logs" element={<NotificationLogs />} />
            <Route path="/manual-notifications" element={<ManualNotificationLogs />} />
            <Route
              path="/view-subscription/:id"
              element={<AddSubscription />}
            />
            <Route
              path="/edit-subscription/:id"
              element={<AddSubscription />}
            />
            <Route path="/manage-cms" element={<AddCms />} />
            <Route
              path="/manage-blogs-events"
              element={<BlogsandEventsList />}
            />
            <Route path="/add-subscription" element={<AddSubscription />} />
            <Route
              path="/view-notifications/:id"
              element={<ViewNotification />}
            />
            <Route path="/edit-role/:id" element={<EditRole />} />
            <Route path="/add-role" element={<AddRole />} />
            <Route
              path="/manage-transaction"
              element={<TransactionListing />}
            />
            <Route
              path="/manage-active-subscription"
              element={<ActiveSubscriptionListing />}
            />
            <Route path="/manage-coupons" element={<ManageCoupons />} />
            <Route
              path="/manage-customer-queries"
              element={<CustomerQueriesListing />}
            />
            <Route path="/add-coupon" element={<AddCoupon />} />
            <Route path="/edit-coupon/:id" element={<AddCoupon />} />
            <Route path="/view-coupon/:id" element={<AddCoupon />} />
            <Route
              path="/manage-deactivated-accounts"
              element={<ManageDeactivatedAccounts />}
            />
            <Route path="/email-management" element={<EmailManagement />} />
            <Route path="/manage-chatbot-sessions" element={<SessionListing />} />
            <Route path="/update-knowledge-base" element={<UpdateKnowledgeBase />} />
          </Route>

          <Route
            path="/sales"
            element={
              <ProtectedRoute authenticationPath="/login" allowedRole="sales" outlet={<SalesPortalMain />} />
            }
          >
            <Route index element={<SalesDashboard />} />
            <Route path="edit-profile" element={<EditProfile />} />
            <Route path="register-entrepreneur" element={<AddEntrepreneur />} />
            <Route path="register-mentor" element={<AddMentor />} />
            <Route path="register-investor" element={<AddInvestor />} />
          </Route>

          <Route
            path="/rm"
            element={
              <ProtectedRoute authenticationPath="/login" allowedRole="rm" outlet={<RmPortalMain />} />
            }
          >
            <Route index element={<RmDashboard />} />
            <Route path="entrepreneurs" element={<RmEntrepreneurs />} />
            <Route
              path="entrepreneurs/details/:id/:type"
              element={<RmProfileDetails />}
            />
            <Route
              path="entrepreneurs/details/:id"
              element={<RmProfileDetails />}
            />
            <Route path="investors" element={<RmInvestors />} />
            <Route
              path="investors/details/:id"
              element={<RmProfileDetails />}
            />
            <Route path="mentors" element={<RmMentors />} />
            <Route
              path="mentors/details/:id"
              element={<RmProfileDetails />}
            />
            <Route path="interaction-logs" element={<RmInteractionLogs />} />
            <Route path="edit-profile" element={<EditProfile />} />
          </Route>
        </Routes>
      </Suspense>
    </div>
  );
};

export default Navigation;
